import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Paper,
  LinearProgress,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material'
import {
  Search,
  LocationOn,
  Star,
  TrendingUp,
  EmojiEvents,
  Explore,
  PlayArrow,
} from '@mui/icons-material'
import { adventuresApi } from '@api/endpoints/adventures'

interface Trail {
  id: string
  name: string
  description: string
  imageUrl?: string
  adventureCount: number
  difficulty: number
  totalDistance: number
  estimatedHours: number
  completionRate: number
  rating: number
  category: string
  isPopular?: boolean
  isFeatured?: boolean
}

// Mock trail data - in production this would come from API
const mockTrails: Trail[] = [
  {
    id: '1',
    name: 'Historic Downtown Discovery',
    description: 'Explore the hidden stories behind downtown landmarks and forgotten architecture',

    adventureCount: 8,
    difficulty: 2,
    totalDistance: 5.2,
    estimatedHours: 4,
    completionRate: 68,
    rating: 4.7,
    category: 'History',
    isPopular: true,
  },
  {
    id: '2',
    name: 'Nature\'s Urban Secrets',
    description: 'Discover hidden parks, gardens, and natural wonders within the city limits',

    adventureCount: 12,
    difficulty: 1,
    totalDistance: 8.5,
    estimatedHours: 6,
    completionRate: 45,
    rating: 4.9,
    category: 'Nature',
    isFeatured: true,
  },
  {
    id: '3',
    name: 'Street Art Expedition',
    description: 'Hunt for vibrant murals and underground art installations across neighborhoods',

    adventureCount: 15,
    difficulty: 2,
    totalDistance: 6.8,
    estimatedHours: 5,
    completionRate: 52,
    rating: 4.5,
    category: 'Art & Culture',
  },
  {
    id: '4',
    name: 'Ghost Signs & Vintage Relics',
    description: 'Track down faded advertisements and vintage storefronts from bygone eras',

    adventureCount: 6,
    difficulty: 3,
    totalDistance: 4.2,
    estimatedHours: 3,
    completionRate: 35,
    rating: 4.3,
    category: 'History',
  },
  {
    id: '5',
    name: 'Culinary Quest',
    description: 'Sample local flavors while discovering food history and hidden eateries',

    adventureCount: 10,
    difficulty: 1,
    totalDistance: 3.5,
    estimatedHours: 4,
    completionRate: 82,
    rating: 4.8,
    category: 'Food & Drink',
    isPopular: true,
  },
  {
    id: '6',
    name: 'Waterfront Wanderer',
    description: 'Follow the river and uncover maritime history, bridges, and waterside secrets',

    adventureCount: 9,
    difficulty: 2,
    totalDistance: 7.1,
    estimatedHours: 5,
    completionRate: 61,
    rating: 4.6,
    category: 'Nature',
  },
]

export default function TrailsPage() {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTrails = mockTrails.filter(
    (trail) =>
      searchQuery === '' ||
      trail.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trail.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trail.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const featuredTrails = mockTrails.filter((t) => t.isFeatured)
  const popularTrails = mockTrails.filter((t) => t.isPopular)

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          🧭 Adventure Trails
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Curated collections of connected adventures that tell a bigger story
        </Typography>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search trails by name, description, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Featured Trails */}
      {featuredTrails.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Star sx={{ color: 'warning.main', mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Featured Trails
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {featuredTrails.map((trail) => (
              <Grid item xs={12} md={6} key={trail.id}>
                <Card
                  sx={{
                    display: 'flex',
                    height: 200,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  {trail.imageUrl ? (
                    <CardMedia
                      component="img"
                      sx={{ width: 200 }}
                      image={trail.imageUrl}
                      alt={trail.name}
                    />
                  ) : (
                    <Box sx={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})` }}>
                      <Explore sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Chip label={trail.category} size="small" color="primary" sx={{ mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {trail.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {trail.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, fontSize: '0.875rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Explore fontSize="small" />
                          {trail.adventureCount} Adventures
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star fontSize="small" sx={{ color: 'warning.main' }} />
                          {trail.rating}
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<PlayArrow />}>
                        Start Trail
                      </Button>
                    </CardActions>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Popular Trails */}
      {popularTrails.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Popular Trails
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {popularTrails.map((trail) => (
              <Grid item xs={12} sm={6} md={4} key={trail.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  {trail.imageUrl ? (
                    <CardMedia
                      component="img"
                      height="160"
                      image={trail.imageUrl}
                      alt={trail.name}
                    />
                  ) : (
                    <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})` }}>
                      <Explore sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Chip label={trail.category} size="small" color="secondary" sx={{ mb: 1 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {trail.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        mb: 2,
                      }}
                    >
                      {trail.description}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Completion Rate
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {trail.completionRate}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={trail.completionRate}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                      }}
                    >
                      <span>{trail.adventureCount} Adventures</span>
                      <span>{trail.estimatedHours}h estimated</span>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" startIcon={<PlayArrow />}>
                      Start Trail
                    </Button>
                    <Button size="small">Details</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* All Trails */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Explore sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            All Trails
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {filteredTrails.map((trail) => (
            <Grid item xs={12} sm={6} md={4} key={trail.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                {trail.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="160"
                    image={trail.imageUrl}
                    alt={trail.name}
                  />
                ) : (
                  <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})` }}>
                    <Explore sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip label={trail.category} size="small" />
                    <Chip
                      label={`Level ${trail.difficulty}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {trail.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                    }}
                  >
                    {trail.description}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Explore fontSize="small" />
                      {trail.adventureCount}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" />
                      {trail.totalDistance} mi
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Star fontSize="small" sx={{ color: 'warning.main' }} />
                      {trail.rating}
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<PlayArrow />} variant="contained">
                    Start Trail
                  </Button>
                  <Button size="small">Details</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {filteredTrails.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No trails found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Paper>
      )}
    </Box>
  )
}
